//#region imports
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { UUID } from "angular2-uuid";
import { forkJoin, Observable, of, Subject } from "rxjs";
import {
  catchError,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import { CameraComponent } from "src/app/annotation/camera/camera.component";
import {
  PictureData,
  PictureUploadState,
} from "src/app/annotation/canvas/canvas.component";
import { GoogleAnalyticsService } from "src/app/google-analytics/google-analytics.service";
import { ConfirmDialogComponent } from "src/app/shared/confirm-dialog/confirm-dialog.component";
import { ImageCompressService } from "src/app/shared/image-compress.service";
import { SurveyPhotoTypeEnum } from "src/app/shared/Models/enums/SurveyPhotoTypeEnum";
import { ChangeInsideSurveyPhotoIndexDTO } from "src/app/shared/Models/write/ChangeInsideSurveyPhotoIndexDto";
import { ChangeOutsideSurveyPhotoIndexDTO } from "src/app/shared/Models/write/ChangeOutsideSurveyPhotoIndexDto";
import { InsidePhotoDto } from "src/app/shared/Models/write/InsidePhotoDto";
import { OutsidePhotoDto } from "src/app/shared/Models/write/OutsidePhotoDto";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ToastService } from "src/app/shared/toast.service";
import { PhotoQueueService } from "../../services/photo-queue.service";
import { SurveyType } from "../../services/photo.service";
import { SavingStateService } from "../../services/saving-state.service";
import { ImageEditorComponent } from "../image-editor/image-editor.component";
import { MultiDeleteComponent } from "./multi-delete/multi-delete.component";
import { PictureInfo } from "./photo-viewmodel";

//#endregion imports

@Component({
  selector: "app-photo-and-remark",
  templateUrl: "./photo-and-remark.component.html",
  styleUrls: ["./photo-and-remark.component.scss"],
})
export class PhotoAndRemarkComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() surveyType: SurveyType;
  @Input() surveyId: string;
  @Input() surveyPhotoType: SurveyPhotoTypeEnum;
  @Input() addressId: string;
  @Input() isAuthCanWrite: boolean;
  @Input() limit: number;
  @Input() photos: PictureInfo[] = [];
  @Input() cameraLayer: HTMLDivElement;
  @Input() camera: CameraComponent;
  @Input() fixedIndex?: number;
  @Input() remarkFieldName?: string = "facadeDetailPhotoRemark";
  @Input() componentDescriptionTitle?: string;
  @Input() isInsidePhoto: boolean;
  @Input() sideBySide: boolean = false;

  identifier: string = UUID.UUID();
  remarkFieldFEName: string;

  @ViewChild("cameraInput") cameraInput: ElementRef<HTMLInputElement>;

  uploadStates = PictureUploadState;
  public selectedImage = false;
  photoOnFocus: PictureInfo;
  droppedDown = false;
  addPhotoIndex = 0;

  constructor(
    private _toastService: ToastService,
    private _modalService: NgbModal,
    private _savingStateService: SavingStateService,
    private _sanitizer: DomSanitizer,
    private _imageCompressor: ImageCompressService,
    private _photoQueue: PhotoQueueService,
    private _loaderService: LoaderService,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) {}

  ngOnInit(): void {
    this.remarkFieldName != "generalTechnicalInfoPhotoRemark"
      ? (this.remarkFieldFEName = this.remarkFieldName)
      : (this.remarkFieldFEName = "proximusEquipmentPhotoRemark");
    this.photos
      .filter((p) => this.fixedIndex == undefined || p.index == this.fixedIndex)
      .forEach((photo) => {
        if (photo.data?.url?.startsWith("blob:")) {
          URL.revokeObjectURL(photo.data.url);
          photo.data.url = URL.createObjectURL(photo.data.blob);
        }
      });

    this.photoOnFocus = this.initialPhoto();
  }

  private initialPhoto(): PictureInfo {
    if (this.fixedIndex != null)
      return this.photos?.find((pi) => pi.index == this.fixedIndex);
    return this.photos[0];
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.photos
      .filter((p) => this.fixedIndex == undefined || p.index == this.fixedIndex)
      .forEach((photo) => {
        if (photo.data?.url?.startsWith("blob:"))
          URL.revokeObjectURL(photo.data.url);
      });
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.init();
  }

  private init() {
    if (this.photos?.length > 0 && this.fixedIndex == null)
      this.updatePhotoIndices();
  }

  private initCamera() {
    this.registerCameraClick();

    this.camera.onSnap &&
      this.camera.onSnap.pipe(first()).subscribe((file) => {
        this.closeLayer(this.cameraLayer);
        if (file) this.addImages([file]);
      });

    // this.camera.maxSizeInKb = environment.maxPhotoSizeInKb;
  }

  registerCameraClick() {
    this._googleAnalyticsService.eventEmitter(
      "open_camera",
      "photos",
      "click_open_camera",
      "click",
      10
    );
  }

  registerGalleryClick() {
    this._googleAnalyticsService.eventEmitter(
      "open_gallery",
      "photos",
      "click_open_gallery",
      "click",
      10
    );
  }

  viewPhoto(photo: PictureInfo) {
    if (!photo) return;
    if (document.documentElement.requestFullscreen)
      document.documentElement.requestFullscreen();
    this.photoOnFocus = photo;
    this.selectedImage = true;
  }

  closeSlider(showSlider: boolean) {
    if (document.fullscreenEnabled)
      if (document.fullscreenElement !== null) document.exitFullscreen();
    this.selectedImage = showSlider;
  }

  showCamera(index: number) {
    this.addPhotoIndex = index;
    if (!this.isAuthCanWrite) return;
    if (!this.camera.hasCamera) {
      this._toastService.showWarnToast(
        "No camera detected",
        "A camera could not be detected on this device."
      );
      return;
    }
    this.initCamera();

    this.camera.start();
    this.openLayer(this.cameraLayer);
  }

  getPhotos(): PictureInfo[] {
    if (this.fixedIndex != null)
      return this.photos.filter((photo) => photo.index === this.fixedIndex);
    return this.photos;
  }

  checkCarouselPosition(imgIndex: number): boolean {
    if (imgIndex >= this.photoOnFocus.index)
      return imgIndex - this.photoOnFocus.index < 3;
    if (imgIndex < this.photoOnFocus.index)
      return this.photoOnFocus.index - imgIndex < 3;
  }

  selectCarouselImage(image: PictureInfo) {
    this.photoOnFocus = image;
  }

  moveCarouselImage(value: number) {
    var newValue = this.photoOnFocus.index + value;
    if (newValue < 0) this.photoOnFocus = this.photos[this.photos.length - 1];
    else if (newValue >= this.photos.length) this.photoOnFocus = this.photos[0];
    else this.photoOnFocus = this.photos[newValue];
  }

  actionsDisabled(writePermission: boolean = false) {
    return !this.photos?.length || (writePermission && !this.isAuthCanWrite);
  }

  showMultiPhotoActions(writePermission: boolean = false) {
    return (
      !this.actionsDisabled(writePermission) &&
      this.photos?.length > 1 &&
      this.fixedIndex == undefined
    );
  }

  disableSinglePhotoActions() {
    return this.fixedIndex == null
      ? !this.photos.length
      : !this.photos[this.fixedIndex];
  }

  openLayer(layer: HTMLDivElement) {
    layer.classList.remove("hidden");
  }

  closeLayer(layer: HTMLDivElement) {
    layer.classList.add("hidden");
  }

  async addCameraImage() {
    if (!this.isAuthCanWrite) return;
    let cameraInputFiles = this.cameraInput.nativeElement.files;

    await this.addImages(cameraInputFiles);
  }

  async addGalleryImage(event: Event, index: number) {
    const element = event.currentTarget as HTMLInputElement;
    this.addPhotoIndex = index;
    if (!this.isAuthCanWrite) return;
    let galleryInputFiles = element.files;

    await this.addImages(galleryInputFiles);

    // nedeed to reupload
    element.value = null;
  }

  async addImages(files: FileList | File[]) {
    if (!this.isAuthCanWrite) return;

    if (
      this.photos.length < this.limit &&
      this.photos.length + files.length > this.limit
    ) {
      const modalRef = this._modalService.open(ConfirmDialogComponent);
      modalRef.componentInstance.title = $localize`:@@validation.photo.uploadlimitexceeded:Upload limit exceeded`;
      modalRef.componentInstance.message = this.photos.length
        ? $localize`:@@validation.photo.uploadlimitexceeded.message:In this section you can upload up to ${
            this.limit
          } photos. Currently ${
            this.photos.length
          } photos have already been uploaded. Please limit your selection to upload additional photos to ${
            this.limit - this.photos.length
          }.`
        : $localize`:@@validation.photo.uploadlimitexceeded.message.nouploads:In this section you can upload a maximum of ${this.limit} photos. Please limit your selection accordingly.`;
      modalRef.componentInstance.cancelBtnTxt = $localize`:@@general.ok:Ok`;
      modalRef.componentInstance.cancelBtnClass = "btn btn-primary";
      modalRef.componentInstance.confirmBtnClass = "hidden";
      return;
    }

    for (let i = 0; i < files.length; i++) {
      let isOverwrite = false;
      if (this.fixedIndex != null) {
        if (this.photos.find((photo) => photo.index === this.fixedIndex))
          isOverwrite = true;
      } else {
        isOverwrite = this.photos.length >= this.limit;
      }
      let file = files[i];
      let index = this.getNewIndex(isOverwrite);
      if (!this.isInsidePhoto)
        if (isOverwrite)
          this.overwritePhoto(file, index, this.createOutsidePhoto.bind(this));
        else this.createOutsidePhoto(file, index);
      else if (this.isInsidePhoto)
        if (isOverwrite)
          this.overwritePhoto(file, index, this.createInsidePhoto.bind(this));
        else this.createInsidePhoto(file, index);
    }
  }

  private getNewIndex(isOverwrite: boolean = false): number {
    if (this.fixedIndex != null) return this.fixedIndex;

    if (!isOverwrite)
      this.addPhotoIndex = (this.addPhotoIndex + 1) % this.limit;
    return this.addPhotoIndex;
  }

  private pushPhotoOnIndex(pictureData: PictureInfo) {
    this.photos.forEach((v, i) => {
      if (i >= pictureData.index) v.index++;
    });
    this.photos.splice(pictureData.index, 0, pictureData);
  }

  private initPictureData(): PictureData {
    const metadata = {
      angle: 0,
      flipX: false,
      flipY: false,
      zoom: 1,
    };
    return {
      id: UUID.UUID(),
      type: this.surveyPhotoType,
      backgroundImageMetadata: JSON.stringify(metadata),
      items: [],
      state: PictureUploadState.ongoing,
    } as PictureData;
  }

  private async addPhoto(
    file: File,
    data: PictureData,
    dto: OutsidePhotoDto | InsidePhotoDto
  ) {
    if (this.photos.length >= this.limit)
      if (this.fixedIndex != null) {
        var existingPhoto = this.photos.find(
          (e) => e.index === this.fixedIndex
        );
        if (existingPhoto) {
          var existingIndex = this.photos.indexOf(existingPhoto);
          this.photos.splice(existingIndex, 1);
        }
      } else {
        this.photos.pop();
      }
    var pictureData = new PictureInfo({
      data,
      facadeDetailPhotoRemark: "",
      index: dto.index,
    });
    this.pushPhotoOnIndex(pictureData);
    this.photoOnFocus = pictureData;
    this._loaderService.show();
    this._imageCompressor.handleImageUpload(file).then((compressedFile) => {
      this._loaderService.hide();
      if (compressedFile != undefined) {
        data.originalBlob = compressedFile;
        dto.originalBlob = compressedFile;
        data.blob = compressedFile;
        dto.blob = compressedFile;
        data.url = URL.createObjectURL(data.blob);
        this._photoQueue.savePhoto(
          dto,
          this.surveyType,
          () => (data.state = PictureUploadState.ongoing),
          () => (data.state = PictureUploadState.done),
          () => (data.state = PictureUploadState.failed)
        );
        this.updatePhotoIndices();
      } else {
        this._toastService.showErrorToast(
          $localize`:@@validation.somethingwhentwrong:Something went wrong!`,
          $localize`:@@validation.photo.corruptedimage:Corrupted image file can not be uploaded!`
        );
        this.photoOnFocus = null;
        let pI = this.photos.findIndex((x) => x.data.id != data.id);
        if (pI != -1) this.photos.splice(pI, 1);
        this.updatePhotoIndices();
      }
    });
  }

  transform(value: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustResourceUrl(value);
  }

  private async createOutsidePhoto(file: File, index: number) {
    let data = this.initPictureData();
    let dto = new OutsidePhotoDto({
      id: data.id,
      surveyId: this.surveyId,
      addressId: this.addressId,
      type: this.surveyPhotoType,
      points: data.items,
      backgroundImageMetadata: data.backgroundImageMetadata,
      index: index,
    });
    await this.addPhoto(file, data, dto);
  }

  private async createInsidePhoto(file: File, index: number) {
    let data = this.initPictureData();
    let dto = new InsidePhotoDto({
      id: data.id,
      surveyId: this.surveyId,
      type: this.surveyPhotoType,
      items: data.items,
      backgroundImageMetadata: data.backgroundImageMetadata,
      index: index,
    });
    await this.addPhoto(file, data, dto);
  }

  async overwritePhoto(
    file: File,
    index: number,
    callback: (file: File, index: number) => void
  ): Promise<void> {
    const modalRef = this._modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message = $localize`:@@validation.photo.overwrite:In this section the maximum number of images you can upload = 1. Would you like to replace the existing photo by the new photo you have selected?`;
    modalRef.componentInstance.confirmBtnTxt = "overwrite";
    modalRef.componentInstance.confirmBtnClass = "btn btn-danger";
    modalRef.componentInstance.cancelBtnClass = "btn btn-primary-no-bg";
    return modalRef.componentInstance.decision
      .pipe(
        first(),
        filter((decision: boolean) => decision),
        switchMap(() =>
          this.deletePhoto(this.photos.find((p) => p.index == index))
        ),
        tap(() => callback(file, index))
      )
      .toPromise();
  }

  reupload(img: PictureInfo): void {
    if (!navigator.onLine) {
      this._toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetreupload:Reuploading images is not possible while you're offline.`
      );
      return;
    }
    this._savingStateService.executeRemoves().pipe(first()).subscribe();
    this._photoQueue.retryFailed(img.data.id);
  }

  canChangePosition(forward: boolean, item: PictureInfo) {
    const index1 = this.photos.findIndex((p) => p == item);
    return forward ? index1 < this.photos.length - 1 : index1 > 0;
  }

  changePhotoIndex(forward: boolean, item: PictureInfo) {
    if (!this.canChangePosition(forward, item)) return;

    if (this.photos.length > 1) {
      const index1 = this.photos.findIndex((p) => p == item);
      const index2 = forward ? index1 + 1 : index1 - 1;
      this.photos.changePhotoIndex(index1, index2);
      this.updatePhotoIndices();
    }
  }

  updatePhotoIndices() {
    if (this.fixedIndex == null) {
      var dto;
      if (this.surveyType == SurveyType.Inside)
        dto = new ChangeInsideSurveyPhotoIndexDTO({
          id: this.identifier,
          photosIndexInfo: this.photos
            .sort((a, b) => a.index - b.index)
            .map((v, i) => {
              v.index = i;
              return {
                photoId: v.data.id,
                index: (v.index = i),
              };
            }),
        });
      else
        dto = new ChangeOutsideSurveyPhotoIndexDTO({
          id: this.identifier,
          photosIndexInfo: this.photos
            .sort((a, b) => a.index - b.index)
            .map((v, i) => {
              v.index = i;
              return {
                photoId: v.data.id,
                index: (v.index = i),
              };
            }),
        });
      this._savingStateService.addPhotoReorder(dto);
    }
  }

  editPhoto(selectedPicture: PictureInfo) {
    if (!this.isAuthCanWrite) return;
    if (!navigator.onLine && selectedPicture.data.state == undefined) {
      this._toastService.showWarnToast(
        $localize`:@@validation.photo.nointernet:No internet`,
        $localize`:@@validation.photo.nointernetediting:Editing existing images is not possible while you're offline.`
      );
      return;
    }

    const modalRef = this._modalService.open(ImageEditorComponent, {
      windowClass: "modal-dialog-overwrite",
    });
    modalRef.componentInstance.photos = this.photos;
    modalRef.componentInstance.remarkFieldFEName = this.remarkFieldFEName;
    modalRef.componentInstance.selectedPhoto = selectedPicture;
    modalRef.componentInstance.surveyType = this.surveyType;
    modalRef.componentInstance.fixedIndex = this.fixedIndex != undefined;
    modalRef.componentInstance.onCancel.asObservable().subscribe((index) => {
      this.photoOnFocus = this.photos.find((x) => x.index == index);
    });
    modalRef.componentInstance.onDelete
      .asObservable()
      .pipe(
        map((index) => this.photos.find((x) => x.index == index)),
        mergeMap((photo) => this.deletePhoto(photo))
      )
      .subscribe(() => (this.photoOnFocus = null));
    modalRef.componentInstance.onSave
      .asObservable()
      .subscribe(({ data, selectedPhoto }) => {
        selectedPicture = selectedPhoto;
        let dto: OutsidePhotoDto | InsidePhotoDto;
        if (!this.isInsidePhoto)
          dto = new OutsidePhotoDto({
            id: selectedPicture.data.id,
            surveyId: this.surveyId,
            addressId: this.addressId,
            originalBlob: data.originalBlob,
            blob: data.blob,
            type: selectedPicture.data.type,
            points: data.items,
            backgroundImageMetadata: data.backgroundImageMetadata,
          });
        else
          dto = new InsidePhotoDto({
            id: selectedPicture.data.id,
            surveyId: this.surveyId,
            originalBlob: data.originalBlob,
            blob: data.blob,
            type: selectedPicture.data.type,
            items: data.items,
            backgroundImageMetadata: data.backgroundImageMetadata,
          });

        this._photoQueue.savePhoto(
          dto,
          this.surveyType,
          () => (selectedPicture.data.state = PictureUploadState.ongoing),
          () => (selectedPicture.data.state = PictureUploadState.done),
          () => (selectedPicture.data.state = PictureUploadState.failed)
        );
        if (selectedPicture.data.originalBlob)
          URL.revokeObjectURL(selectedPicture.data.url);
        selectedPicture.data = {
          ...selectedPicture.data,
          ...data,
          originalBlob: selectedPicture.data.originalBlob,
          url: URL.createObjectURL(data.blob),
        };
      });
  }

  toggleDropDown(photo: PictureInfo) {
    this.photoOnFocus = photo;
    this.droppedDown = !this.droppedDown;
  }

  deleteSelectedPhoto(photo: PictureInfo) {
    if (!this.isAuthCanWrite || !photo) return;
    const modalRef = this._modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message = $localize`:@@validation.photo.aknowledgedelete:Are you sure you want to delete the photo from LST?`;
    modalRef.componentInstance.confirmBtnTxt = "delete";
    modalRef.componentInstance.confirmBtnClass = "btn btn-danger";
    modalRef.componentInstance.cancelBtnClass = "btn btn-primary-no-bg";
    modalRef.componentInstance.decision
      .pipe(
        first(),
        filter((decision: boolean) => decision),
        switchMap(() => this.deletePhoto(photo))
      )
      .subscribe(() => {
        this.photoOnFocus = this.photos.find(
          (p) =>
            p.index ==
            (this.fixedIndex != undefined
              ? this.fixedIndex
              : photo.index == 0
              ? photo.index
              : photo.index - 1)
        );
      });
  }
  deletePhotos() {
    if (!this.isAuthCanWrite) return;
    const modalRef = this._modalService.open(MultiDeleteComponent, {
      windowClass: "multi-delete-modal",
    });
    const comp = modalRef.componentInstance as MultiDeleteComponent;
    comp.photos = this.photos;
    const sub = comp.selectedPhotos.asObservable().subscribe((photos) => {
      const confirmModalRef = this._modalService.open(ConfirmDialogComponent);
      const confirmComp =
        confirmModalRef.componentInstance as ConfirmDialogComponent;
      confirmComp.message = $localize`:@@validation.photo.aknowledgemultidelete:Are you sure you want to delete these photos?`;
      confirmComp.confirmBtnTxt = "delete";
      confirmComp.confirmBtnClass = "btn btn-danger";
      confirmComp.cancelBtnClass = "btn btn-primary-no-bg";
      confirmComp.decision
        .pipe(
          first(),
          filter((decision) => decision),
          tap(() => this._loaderService.show()),
          switchMap(() => forkJoin(photos.map((p) => this.deletePhoto(p))))
        )
        .subscribe(() => {
          let highestIndex = this.photos.find(
            (p) => p.index == Math.max(...this.photos.map((p) => p.index))
          )?.index;
          if (highestIndex == undefined) this.photoOnFocus = undefined;
          else
            this.photoOnFocus =
              this.photos[
                this.photoOnFocus?.index > highestIndex
                  ? highestIndex
                  : this.photoOnFocus.index
              ];
          modalRef.close();
          sub.unsubscribe();
          this._loaderService.hide();
        });
    });
    comp.cancel
      .asObservable()
      .pipe(first())
      .subscribe(() => {
        modalRef.close();
        sub.unsubscribe();
      });
  }

  onRemove(photo: PictureInfo, offline?: boolean) {
    if (offline)
      this._savingStateService.addPhotoToRemove(photo.data.id, this.surveyType);
    if (!this.fixedIndex) {
      const index = this.photos.findIndex((p) => p.data.id == photo.data.id);
      if (index !== -1) this.photos.splice(index, 1);
    } else {
      let pI = this.photos.findIndex((photo) => photo.index == this.fixedIndex);
      if (pI != -1) this.photos.splice(pI, 1);
    }
    this.updatePhotoIndices();
  }

  deletePhoto(photo: PictureInfo) {
    if (!photo) return of(true);
    if (photo?.data != null && photo.data.state != PictureUploadState.ongoing)
      if (navigator.onLine)
        return this._photoQueue
          .removePhoto(
            photo.data.id,
            this.surveyType,
            photo.data.state != PictureUploadState.failed
          )
          .pipe(
            first(),
            tap(() => this.onRemove(photo)),
            catchError((e) => {
              this.onRemove(photo, true);
              return of(e);
            })
          );
      else
        return new Observable((sub) => {
          this.onRemove(photo, true);
          sub.next(null);
          sub.complete();
        });
    else
      return new Observable((sub) => {
        this._toastService.showWarnToast(
          $localize`:@@validation.photo.imageuploading:Image is still uploading.`,
          $localize`:@@validation.photo.cantdeletewhileuploading:You cant delete an image that is still uploading.`
        );
        sub.next(null);
        sub.complete();
      });
  }

  onRemarkChanged(image: PictureInfo, val: string) {
    if (!this.isAuthCanWrite) return;
    image[this.remarkFieldName] = val;
    image.remarkChanged = true;
  }
}
